/**
 * Platform Tracking Configuration — SINGLE SOURCE OF TRUTH
 *
 * Drives tracking for INTERNAL PLATFORM EVENTS ONLY (the logged-in Vue app).
 * The public marketing site has its own separate tag setup — the two surfaces
 * may share pixel IDs and Ads account IDs but the event wiring is different.
 * Do NOT copy marketing-site event→label mappings here verbatim; only add
 * labels for Ads conversion actions that actually fire from APP code.
 *
 * To add a new Meta Pixel, GA4 property, Google Ads account, conversion label,
 * or funnel event — edit THIS file. `utils/metaPixel.ts` is pure logic.
 *
 * ─── Where things live ───────────────────────────────────────────────────────
 *  Frontend IDs/labels/events  → this file
 *  Frontend tracker logic      → src/utils/metaPixel.ts
 *  Backend CAPI secrets        → backend/.env (META_CONVERSIONS_TOKEN_US)
 *  Backend CAPI service        → backend/services/integrations/metaConversionsService.js
 *  Backend event relay         → backend/controllers/integrations/metaPixelController.js
 *
 * ─── Google Ads setup ────────────────────────────────────────────────────────
 *  Fresh campaigns run on the BACKUP account (AW-18107537655); it receives a
 *  gtag('config') call so audiences/remarketing work.
 *
 *  What fires from the app today:
 *    CompleteRegistration → backup "Begin checkout". Fires post-Auth0 callback
 *                           (AuthInitPage) with email user_data so enhanced
 *                           conversions can match clicks.
 *    Activation           → new conversion on workspace creation (custom event).
 *                           Wired with email user_data; no Ads label yet — add
 *                           one once the conversion action exists in Ads.
 *    Purchase             → backup "Purchase".
 *
 *  NOTE: Begin checkout (58cuCLr_naAcEPexrLpD) used to fire from the public
 *  marketing site on register-click. That marketing-site fire MUST be removed
 *  once this app-side change is live, otherwise the conversion double-counts.
 */

export interface TrackingConfig {
  pixelId: string
  ga4Id: string
  googleAdsId?: string
  googleAdsLabels?: Record<string, string>
}

/** Primary tracking IDs */
export const TRACKING_CONFIG: TrackingConfig = {
  pixelId: '1149697969793683',
  ga4Id: 'G-70ZCYQ23J1',
}

/**
 * Backup Google Ads account. Any event name present in `labels` fires a
 * conversion to this account IN ADDITION to any primary account conversion
 * for the same event.
 */
export const GOOGLE_ADS_BACKUP: { id: string; labels: Record<string, string> } = {
  id: 'AW-18107537655',
  labels: {
    // "Begin checkout" action on AW-18107537655 — moved from marketing-site
    // register-click to app-side post-Auth0 callback so enhanced conversions
    // can attach the user's email (gtag handles SHA-256 hashing automatically).
    CompleteRegistration: '58cuCLr_naAcEPexrLpD',
    // "Purchase" action on AW-18107537655
    Purchase: 'Kdu1CLbyoKAcEPexrLpD',
  },
}

/** Meta Pixel event name → GA4 event name (GA4 has its own recommended names) */
export const GA4_EVENT_MAP: Record<string, string> = {
  PageView: 'page_view',
  ViewContent: 'view_item',
  Lead: 'generate_lead',
  CompleteRegistration: 'sign_up',
  InitiateCheckout: 'begin_checkout',
  Purchase: 'purchase',
  StartTrial: 'start_trial',
  CreatePage: 'create_page',
  Activation: 'activation',
  // Page-creation sub-funnel — feeds the admin "Page Creation Funnel" widget
  // so we can see where users drop off between opening the wizard and publishing.
  CreatePageStarted: 'create_page_started',
  CreatePageSourceSelected: 'create_page_source_selected',
  CreatePageThemeSelected: 'create_page_theme_selected',
  PagePublishStarted: 'page_publish_started',
}
